import React, { useEffect, useState } from "react";
import { Users, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState, Loader, StatusBadge } from "@/components/dashboard/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useSelect, SelectCheck, SelectionToolbar } from "@/components/admin/BulkBar";
import { trashAction } from "@/lib/trash";
import { AdminSetPasswordDialog } from "@/components/PasswordDialogs";
import { KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminClients() {
  const { hasRole } = useAuth();
  const [clients, setClients] = useState(null);
  const [detail, setDetail] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [arch, setArch] = useState(false);
  const sel = useSelect();
  const isSuper = hasRole("super_admin");
  const load = () => api.get(`/clients?include_archived=${arch}`).then(({ data }) => setClients(data || []));
  useEffect(() => { sel.clear(); load(); /* eslint-disable-next-line */ }, [arch]);
  const open = async (c) => { const { data } = await api.get(`/clients/${c.user_id}`); setDetail(data); };
  const delClient = async (uid) => {
    if (!window.confirm("Déplacer ce client vers la corbeille ?")) return;
    try { await trashAction("clients", [uid], "delete"); toast.success("Client déplacé vers la corbeille"); setDetail(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  if (clients === null) return <Loader />;
  return (
    <div>
      <PageHeader title="Clients" subtitle="Tous les médecins et cabinets inscrits." />
      <SelectionToolbar items={clients} idKey="user_id" sel={sel} entity="clients" onDone={load} label="client" archived={arch} onToggleArchived={() => setArch((a) => !a)} />
      {clients.length === 0 ? <EmptyState icon={Users} title={arch ? "Aucun client archivé" : "Aucun client"} /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{clients.map((c) => (
          <div key={c.user_id} role="button" tabIndex={0} onClick={() => open(c)} className={`relative bg-white rounded-xl border p-5 pl-11 text-left hover:shadow-md transition-all cursor-pointer ${sel.has(c.user_id) ? "border-primary ring-1 ring-primary/30" : "border-slate-200"}`} data-testid={`client-${c.user_id}`}>
            <SelectCheck className="absolute left-3 top-5" checked={sel.has(c.user_id)} onChange={() => sel.toggle(c.user_id)} testId={`select-client-${c.user_id}`} />
            <div className="flex items-center gap-3">
              <Avatar className="h-11 w-11"><AvatarFallback className="bg-primary/10 text-primary font-semibold">{(c.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
              <div className="min-w-0"><p className="font-semibold text-slate-900 truncate">{c.name}</p><p className="text-sm text-slate-500 truncate">{c.practice || "—"}</p></div>
            </div>
            <p className="mt-3 text-sm text-slate-500">{c.email}</p>
            <p className="text-sm text-slate-500">{c.city} {c.canton}</p>
          </div>))}
        </div>
      )}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto custom-scroll">
          {detail && (<>
            <DialogHeader><DialogTitle>{detail.client.name}</DialogTitle></DialogHeader>
            <div className="text-sm space-y-1 text-slate-600">
              <p>{detail.client.email} · {detail.client.phone}</p>
              <p>{detail.client.practice} — {detail.client.city} {detail.client.canton}</p>
            </div>
            <div className="mt-4"><h4 className="font-semibold text-sm mb-2">Demandes ({detail.requests.length})</h4>
              {detail.requests.map((r) => <div key={r.request_id} className="flex items-center justify-between py-1.5 text-sm border-b border-slate-50"><span>{(r.services || []).join(", ") || "Demande"}</span><StatusBadge status={r.status} /></div>)}
              {detail.requests.length === 0 && <p className="text-sm text-slate-400">Aucune</p>}
            </div>
            <div className="mt-4"><h4 className="font-semibold text-sm mb-2">Missions ({detail.missions.length})</h4>
              {detail.missions.map((m) => <div key={m.mission_id} className="flex items-center justify-between py-1.5 text-sm border-b border-slate-50"><span>{new Date(m.date).toLocaleDateString("fr-CH")} {m.start}–{m.end}</span><StatusBadge status={m.status} /></div>)}
              {detail.missions.length === 0 && <p className="text-sm text-slate-400">Aucune</p>}
            </div>
            <DialogFooter>
              {isSuper && <Button variant="outline" onClick={() => setPwOpen(true)} data-testid="client-reset-password"><KeyRound className="h-4 w-4 mr-1.5" />Mot de passe</Button>}
              <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => delClient(detail.client.user_id)} data-testid="delete-client-btn"><Trash2 className="h-4 w-4 mr-1.5" />Déplacer vers la corbeille</Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
      {detail && <AdminSetPasswordDialog open={pwOpen} onOpenChange={setPwOpen} userId={detail.client.user_id} userName={detail.client.name} />}
    </div>
  );
}
